// إعدادات الحساب — تعديل الاسم · تغيير الصورة · تغيير كلمة المرور (RTL)
import { useEffect, useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { motion } from "framer-motion";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { AV_FALLBACK } from "@/components/Widgets";
import { errText } from "@/v2/lib/hooks";
import {
  X, User, ImagePlus, KeyRound, Save, Upload, Eye, EyeOff, ShieldCheck, Loader2,
} from "lucide-react";

const TABS = [
  { key: "name", label: "بياناتي", icon: User },
  { key: "avatar", label: "الصورة", icon: ImagePlus },
  { key: "password", label: "كلمة المرور", icon: KeyRound },
];

export default function ProfileSettings({ onClose }) {
  const { user, refresh } = useAuth();
  const qc = useQueryClient();
  const [tab, setTab] = useState("name");
  const [name, setName] = useState(user?.name || "");
  const [workType, setWorkType] = useState(user?.work_type || "");
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState("");
  const [file, setFile] = useState(null);
  const [pwd, setPwd] = useState({ current: "", next: "", confirm: "" });
  const [show, setShow] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const done = async (msg) => {
    await refresh();
    qc.invalidateQueries({ queryKey: ["profile"] });
    toast.success(msg);
  };

  const saveName = async () => {
    if (name.trim().length < 3) return toast.error("الاسم مطلوب (3 أحرف على الأقل)");
    setBusy(true);
    try {
      await api.patch("/auth/profile", { name: name.trim() });
      if (workType) await api.patch("/auth/work-type", { work_type: workType });
      await qc.invalidateQueries();
      await done("تم تحديث بياناتك");
      onClose();
    } catch (e) { toast.error(errText(e, "تعذّر تحديث الاسم")); } finally { setBusy(false); }
  };

  const pickFile = (f) => {
    if (!f) return;
    if (!/^image\/(jpeg|png|webp)$/.test(f.type)) return toast.error("اختر صورة JPG أو PNG أو WEBP");
    if (f.size > 5 * 1024 * 1024) return toast.error("حجم الصورة يجب أن يكون أقل من 5 ميجابايت");
    setFile(f);
    setPreview(URL.createObjectURL(f));
  };

  const saveAvatar = async () => {
    if (!file) return toast.error("اختر صورة أولًا");
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post("/auth/avatar", fd, { headers: { "Content-Type": "multipart/form-data" } });
      await done("تم تحديث صورتك الشخصية");
      onClose();
    } catch (e) { toast.error(errText(e, "تعذّر رفع الصورة")); } finally { setBusy(false); }
  };

  const savePassword = async () => {
    if (!pwd.current) return toast.error("أدخل كلمة المرور الحالية");
    if (pwd.next.length < 6) return toast.error("كلمة المرور الجديدة 6 أحرف على الأقل");
    if (pwd.next !== pwd.confirm) return toast.error("كلمة المرور الجديدة وتأكيدها غير متطابقين");
    setBusy(true);
    try {
      const { data } = await api.post("/auth/change-password", {
        current_password: pwd.current, new_password: pwd.next,
      });
      if (data.token) localStorage.setItem("hrm_token", data.token);
      setPwd({ current: "", next: "", confirm: "" });
      await done("تم تغيير كلمة المرور بنجاح");
      onClose();
    } catch (e) { toast.error(errText(e, "تعذّر تغيير كلمة المرور")); } finally { setBusy(false); }
  };

  return (
    <div className="v2-overlay flex items-start justify-center p-4 overflow-y-auto" onClick={onClose}>
      <motion.div initial={{ opacity: 0, y: 18, scale: 0.97 }} animate={{ opacity: 1, y: 0, scale: 1 }}
        transition={{ duration: 0.28 }} onClick={(e) => e.stopPropagation()}
        className="v2-glass v2-glass-gold w-full max-w-lg my-8 p-6" data-testid="profile-settings-modal" dir="rtl">
        <div className="flex items-center justify-between mb-5">
          <h3 className="text-xl font-extrabold">إعدادات الحساب</h3>
          <button onClick={onClose} data-testid="settings-close"
            className="w-9 h-9 rounded-full v2-inset flex items-center justify-center hover:opacity-80"><X size={18} /></button>
        </div>

        <div className="flex gap-2 mb-6">
          {TABS.map((t) => (
            <button key={t.key} onClick={() => setTab(t.key)} data-testid={`settings-tab-${t.key}`}
              className={`flex-1 flex items-center justify-center gap-2 py-2.5 rounded-2xl text-sm font-bold border transition-colors ${
                tab === t.key ? "border-beige/60 bg-beige/12 text-beige-ink"
                  : "border-line bg-surface-2 text-ink-2 hover:text-ink"}`}>
              <t.icon size={15} /> {t.label}
            </button>
          ))}
        </div>

        {tab === "name" && (
          <div className="space-y-4" data-testid="settings-name-section">
            <div>
              <label className="v2-label">الاسم المعروض</label>
              <input value={name} onChange={(e) => setName(e.target.value)} data-testid="settings-name-input"
                className="v2-input" placeholder="مثال: أحمد عبدالله المالكي" />
            </div>
            <label className="block space-y-2 text-sm"><span>طبيعة العمل</span><select className="v2-input" disabled={!!user?.work_type && !!user?.masar?.profile_id && user?.onboarding_done !== false && !user?.profile_change_allowed} value={workType} onChange={e => setWorkType(e.target.value)}><option value="">اختر طبيعة العمل</option><option value="field">ميداني</option><option value="office">مكتبي</option></select></label>
            <p className="text-xs text-ink-2 leading-relaxed v2-inset p-3">
              هذا اسمك في مسار وتميز، وتستخدمه أيضًا لتسجيل الدخول.
            </p>
            <button onClick={saveName} disabled={busy} data-testid="settings-name-save" className="v2-btn w-full">
              {busy ? <Loader2 size={17} className="animate-spin" /> : <Save size={17} />} حفظ البيانات
            </button>
          </div>
        )}

        {tab === "avatar" && (
          <div className="space-y-4" data-testid="settings-avatar-section">
            <div className="flex items-center gap-5">
              <img src={preview || user?.avatar} alt={user?.name} onError={AV_FALLBACK}
                data-testid="settings-avatar-preview"
                className="w-24 h-24 rounded-full object-cover border-2 border-beige/50" />
              <div className="flex-1">
                <button onClick={() => fileRef.current?.click()} data-testid="settings-avatar-pick" className="v2-btn-ghost w-full">
                  <Upload size={16} /> اختيار صورة
                </button>
                <p className="text-xs text-ink-2 mt-2">JPG أو PNG أو WEBP — حتى 5 ميجابايت</p>
                {file && <p className="text-xs v2-emerald-text mt-1 truncate" data-testid="settings-avatar-name">{file.name}</p>}
              </div>
            </div>
            <input ref={fileRef} type="file" accept="image/jpeg,image/png,image/webp" className="hidden"
              data-testid="settings-avatar-input" onChange={(e) => pickFile(e.target.files?.[0])} />
            <button onClick={saveAvatar} disabled={busy || !file} data-testid="settings-avatar-save"
              className={`v2-btn w-full ${busy || !file ? "opacity-40 grayscale cursor-not-allowed" : ""}`}>
              {busy ? <Loader2 size={17} className="animate-spin" /> : <ImagePlus size={17} />} حفظ الصورة
            </button>
          </div>
        )}

        {tab === "password" && (
          <div className="space-y-4" data-testid="settings-password-section">
            <div>
              <label className="v2-label">كلمة المرور الحالية</label>
              <div className="relative">
                <input type={show ? "text" : "password"} value={pwd.current} data-testid="settings-pwd-current"
                  onChange={(e) => setPwd({ ...pwd, current: e.target.value })} className="v2-input !pe-12" />
                <button type="button" onClick={() => setShow(!show)} data-testid="settings-pwd-toggle"
                  className="absolute inset-y-0 left-3 flex items-center text-ink-2">{show ? <EyeOff size={18} /> : <Eye size={18} />}</button>
              </div>
            </div>
            <div>
              <label className="v2-label">كلمة المرور الجديدة</label>
              <input type={show ? "text" : "password"} value={pwd.next} data-testid="settings-pwd-new"
                onChange={(e) => setPwd({ ...pwd, next: e.target.value })} className="v2-input" />
            </div>
            <div>
              <label className="v2-label">تأكيد كلمة المرور الجديدة</label>
              <input type={show ? "text" : "password"} value={pwd.confirm} data-testid="settings-pwd-confirm"
                onChange={(e) => setPwd({ ...pwd, confirm: e.target.value })} className="v2-input" />
            </div>
            <p className="text-xs text-ink-2 flex items-start gap-2 v2-inset p-3">
              <ShieldCheck size={15} className="text-ok shrink-0 mt-0.5" />
              6 أحرف على الأقل، ويُفضّل ألا تكون مستخدمة في حساب آخر. بعد 5 محاولات خاطئة يتوقف التغيير مؤقتًا للحماية.
            </p>
            <button onClick={savePassword} disabled={busy} data-testid="settings-pwd-save" className="v2-btn w-full">
              {busy ? <Loader2 size={17} className="animate-spin" /> : <KeyRound size={17} />} تغيير كلمة المرور
            </button>
          </div>
        )}
      </motion.div>
    </div>
  );
}
